import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

export const TransitionAnimationProps = {
	in: PropTypes.bool,
	className: PropTypes.string,
	timing: PropTypes.number,
	children: PropTypes.element.isRequired,
	stayMountedWhenExited: PropTypes.bool,
	suffix: PropTypes.exact({
		enter: PropTypes.string.isRequired,
		exit: PropTypes.string.isRequired
	}),
	onEnter: PropTypes.func,
	onEntering: PropTypes.func,
	onEntered: PropTypes.func,
	onExit: PropTypes.func,
	onExiting: PropTypes.func,
	onExited: PropTypes.func,
	onMounted: PropTypes.func,
	allowRef: PropTypes.bool
};

export const TransitionAnimationDefaultProps = {
	in: false,
	className: '',
	stayMountedWhenExited: false,
	animationStyle: 'animation',
	suffix: { enter: '-in', exit: '-out' },
	timing: 1000,
	allowRef: false
};

export const TransitionAnimation = ({
	in: show,
	className,
	timing,
	children,
	stayMountedWhenExited,
	animationStyle,
	suffix,
	onEnter,
	onEntering,
	onEntered,
	onExit,
	onExiting,
	onExited,
	onMounted,
	allowRef
}) => {
	const [isMounted, setMounted] = useState(false);

	const [update, setUpdate] = useState({
		active: show,
		/*exit activated*/ exc: false,
		/*enter activated*/ enc: false
	});

	const isAnimation = animationStyle === 'animation';

	const enterClass = className ? `${className}${suffix.enter}` : suffix.enter;

	const exitClass = className ? `${className}${suffix.exit}` : suffix.exit;

	const isRender =
		(!isAnimation && show) ||
		update.enc ||
		update.exc ||
		update.active ||
		update.fixedbug;

	let target;

	useEffect(() => {
		const node = target;
		const isExiting = !show && update.active;
		const isEntering = show && !update.active;
		const isEntered = show && update.active;
		const isExited = !show && !update.active;
		const isAnimationDefaultEnter = !isMounted && isAnimation && update.active;

		isMounted && onMounted && onMounted(node);
		if (!update.exc && isExiting) {
			onExit && onExit(node);
			setUpdate({
				active: true,
				exc: true,
				enc: false,
				timeout: setTimeout(() => {
					setUpdate({ active: false, exc: false, enc: false });
					onExited && onExited(node);
				}, timing)
			});
			onExiting &&
				setTimeout(() => {
					onExiting(node);
				}, 50);
		} else if ((!update.enc && isEntering) || isAnimationDefaultEnter) {
			setTimeout(() => {
				onEnter && onEnter(node);
				onEntering &&
					setTimeout(() => {
						onEntering(node);
					}, 50);
				setUpdate({
					active: false,
					exc: false,
					enc: true,
					timeout: setTimeout(() => {
						if (!isAnimationDefaultEnter)
							setUpdate({ active: true, exc: false, enc: false });
						onEntered && onEntered(node);
					}, timing)
				});
			}, 0);
		}

		if (update.exc && isEntered) {
			clearTimeout(update.timeout);
			setUpdate({ active: false, exc: false, enc: false, fixedbug: true });
		}
		if (update.enc && isExited) {
			clearTimeout(update.timeout);
			setUpdate({ active: true, exc: false, enc: false });
		}
	}, [
		show,
		timing,
		onEnter,
		onEntering,
		onEntered,
		onExit,
		onExiting,
		onExited,
		isAnimation,
		onMounted,
		target,
		update,
		isMounted
	]);

	// cleanup when unmounted while transitioning
	useEffect(() => {
		return () => {
			update.timeout && clearTimeout(update.timeout);
		};
	}, [update.timeout]);

	useEffect(() => {
		setMounted(true);
		return () => setMounted(false);
	}, []);

	const childprops = {
		className:
			(children.props.className ? children.props.className + ' ' : '') +
			((isAnimation
			? show
			: (update.enc || update.active) && show)
				? enterClass
				: exitClass)
	};

	if (allowRef) {
		childprops.ref = (node) => (target = node);
	}

	if (stayMountedWhenExited) {
		const childstyle = children.props.style || {};
		childprops.style = {
			...childstyle,
			display: isRender ? 'block' : 'none'
		};
	}

	return (
		(isRender || stayMountedWhenExited) &&
		React.cloneElement(children, childprops)
	);
};

TransitionAnimation.propTypes = {
	animationStyle: PropTypes.oneOf(['animation', 'transition']),
	...TransitionAnimationProps
};
TransitionAnimation.defaultProps = TransitionAnimationDefaultProps;
